import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { TrendingUp, Clock, Users, Shield } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  // Admin password - change this to your preferred password
  const ADMIN_PASSWORD = 'QRM2026!';

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
      // Store in session
      sessionStorage.setItem('adminAuth', 'true');
    } else {
      setError('Incorrect password. Please try again.');
    }
  };

  useEffect(() => {
    // Check if already authenticated in this session
    if (sessionStorage.getItem('adminAuth') === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching admin stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-100 to-gray-200 flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md p-8 shadow-xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Access</h1>
            <p className="text-gray-600 mt-2">Smart QRM Navigator Dashboard</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="w-full"
                autoFocus
              />
            </div>
            
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}
            
            <Button 
              type="submit" 
              className="w-full bg-indigo-600 hover:bg-indigo-700"
            >
              Access Dashboard
            </Button>
          </form>
          
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Protected area for site administrators only.</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Smart QRM Navigator - Website Statistics & Management</p>
          <Button 
            onClick={fetchStats} 
            className="mt-4 bg-indigo-600 hover:bg-indigo-700"
          >
            <Clock className="mr-2 h-4 w-4" /> Refresh Data
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          <Card className="p-4 bg-gradient-to-br from-indigo-500 to-blue-600 text-white">
            <div className="text-3xl font-bold">{stats?.summary?.total_pageviews || 0}</div>
            <div className="text-indigo-100 text-sm">Page Views</div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-cyan-500 to-teal-600 text-white">
            <div className="text-3xl font-bold">{stats?.summary?.unique_visitors || 0}</div>
            <div className="text-cyan-100 text-sm">Unique Visitors</div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-purple-500 to-indigo-600 text-white">
            <div className="text-3xl font-bold">{stats?.summary?.total_vip_signups || 0}</div>
            <div className="text-purple-100 text-sm">VIP Signups</div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-green-500 to-emerald-600 text-white">
            <div className="text-3xl font-bold">{stats?.summary?.total_contact_submissions || 0}</div>
            <div className="text-green-100 text-sm">Contact Forms</div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-blue-500 to-cyan-600 text-white">
            <div className="text-3xl font-bold">{stats?.summary?.total_partner_clicks || 0}</div>
            <div className="text-blue-100 text-sm">Partner Clicks</div>
          </Card>
        </div>
        
        {/* Secondary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 bg-gradient-to-br from-orange-500 to-amber-600 text-white">
            <div className="text-3xl font-bold">{stats?.summary?.total_trial_users || 0}</div>
            <div className="text-orange-100 text-sm">Trial Users</div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-pink-500 to-rose-600 text-white">
            <div className="text-3xl font-bold">{stats?.summary?.total_calculator_uses || 0}</div>
            <div className="text-pink-100 text-sm">Calculator Uses</div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-teal-500 to-cyan-600 text-white">
            <div className="text-3xl font-bold">{stats?.summary?.total_financial_lab_uses || 0}</div>
            <div className="text-teal-100 text-sm">Financial Lab</div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-violet-500 to-purple-600 text-white">
            <div className="text-3xl font-bold">{stats?.summary?.total_consulting_uses || 0}</div>
            <div className="text-violet-100 text-sm">Consulting</div>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6 border-b border-gray-200 overflow-x-auto">
          {['overview', 'visitors', 'vip-signups', 'contacts', 'partners', 'trials'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === tab 
                  ? 'bg-white text-indigo-600 border border-b-0 border-gray-200' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <Card className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <TrendingUp className="mr-2 h-5 w-5 text-indigo-600" />
                  Partner Clicks Summary
                </h2>
                {Object.keys(stats?.partner_click_summary || {}).length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(stats?.partner_click_summary || {}).map(([partner, clicks]) => (
                      <div key={partner} className="p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">{clicks}</div>
                        <div className="text-sm text-gray-600 truncate">{partner}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">No partner clicks recorded yet.</p>
                )}
              </div>

              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <Users className="mr-2 h-5 w-5 text-purple-600" />
                  Recent VIP Signups
                </h2>
                <div className="space-y-2">
                  {(stats?.vip_signups || []).slice(0, 5).map((signup, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                      <span className="font-medium text-gray-900">{signup.email}</span>
                      <span className="text-sm text-gray-500">{formatDate(signup.signed_up_at)}</span>
                    </div>
                  ))}
                  {(stats?.vip_signups || []).length === 0 && (
                    <p className="text-gray-500">No VIP signups yet.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Visitors Tab */}
          {activeTab === 'visitors' && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Page Views ({stats?.pageviews?.length || 0} total, {stats?.summary?.unique_visitors || 0} unique visitors)
              </h2>
              
              {/* Page View Summary */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Views by Page</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(stats?.page_view_summary || {}).sort((a, b) => b[1] - a[1]).map(([page, count]) => (
                    <div key={page} className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                      <div className="text-xl font-bold text-indigo-600">{count}</div>
                      <div className="text-sm text-gray-600 truncate">{page}</div>
                    </div>
                  ))}
                </div>
                {Object.keys(stats?.page_view_summary || {}).length === 0 && (
                  <p className="text-gray-500 text-sm">No page views recorded yet.</p>
                )}
              </div>
              
              {/* Recent Page Views Table */}
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Recent Visits</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left p-3 font-semibold">Page</th>
                      <th className="text-left p-3 font-semibold">Date & Time</th>
                      <th className="text-left p-3 font-semibold">Location</th>
                      <th className="text-left p-3 font-semibold">IP Address</th>
                      <th className="text-left p-3 font-semibold">Referrer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(stats?.pageviews || []).slice(0, 50).map((pv, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="p-3 font-medium text-indigo-600">{pv.page}</td>
                        <td className="p-3 text-gray-600">{formatDate(pv.timestamp)}</td>
                        <td className="p-3 text-gray-600 text-sm">
                          {pv.city && pv.city !== 'Unknown' 
                            ? `${pv.city}, ${pv.country}` 
                            : pv.country && pv.country !== 'Unknown' 
                              ? pv.country 
                              : 'N/A'}
                        </td>
                        <td className="p-3 text-gray-500 font-mono text-sm">{pv.ip}</td>
                        <td className="p-3 text-gray-500 text-sm truncate max-w-xs">{pv.referrer || 'direct'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(stats?.pageviews || []).length === 0 && (
                  <p className="text-center text-gray-500 py-8">No page views recorded yet. Views will appear after users visit your site.</p>
                )}
              </div>
            </div>
          )}

          {/* VIP Signups Tab */}
          {activeTab === 'vip-signups' && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                All VIP Signups ({stats?.vip_signups?.length || 0})
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left p-3 font-semibold">Email</th>
                      <th className="text-left p-3 font-semibold">Source</th>
                      <th className="text-left p-3 font-semibold">Signed Up</th>
                      <th className="text-left p-3 font-semibold">Notified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(stats?.vip_signups || []).map((signup, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="p-3 font-medium">{signup.email}</td>
                        <td className="p-3 text-gray-600">{signup.source || 'website'}</td>
                        <td className="p-3 text-gray-600">{formatDate(signup.signed_up_at)}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${signup.notified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {signup.notified ? 'Yes' : 'No'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(stats?.vip_signups || []).length === 0 && (
                  <p className="text-center text-gray-500 py-8">No VIP signups yet.</p>
                )}
              </div>
            </div>
          )}

          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Contact Form Submissions ({stats?.contact_submissions?.length || 0})
              </h2>
              <div className="space-y-4">
                {(stats?.contact_submissions || []).map((contact, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="font-bold text-gray-900">{contact.firstName} {contact.lastName}</span>
                        <span className="text-gray-500 ml-2">({contact.email})</span>
                      </div>
                      <span className="text-sm text-gray-500">{formatDate(contact.timestamp)}</span>
                    </div>
                    {contact.company && (
                      <div className="text-sm text-gray-600 mb-1">
                        <strong>Company:</strong> {contact.company}
                      </div>
                    )}
                    {contact.inquiry && (
                      <div className="text-sm text-gray-600 mb-2">
                        <strong>Inquiry Type:</strong> {contact.inquiry}
                      </div>
                    )}
                    <div className="text-gray-700 bg-white p-3 rounded border">
                      {contact.message}
                    </div>
                  </div>
                ))}
                {(stats?.contact_submissions || []).length === 0 && (
                  <p className="text-center text-gray-500 py-8">No contact form submissions yet.</p>
                )}
              </div>
            </div>
          )}

          {/* Partners Tab */}
          {activeTab === 'partners' && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Partner Link Clicks ({stats?.partner_visits?.length || 0})
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left p-3 font-semibold">Partner</th>
                      <th className="text-left p-3 font-semibold">Clicked At</th>
                      <th className="text-left p-3 font-semibold">User IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(stats?.partner_visits || []).map((visit, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="p-3 font-medium">{visit.partner_name}</td>
                        <td className="p-3 text-gray-600">{formatDate(visit.timestamp)}</td>
                        <td className="p-3 text-gray-500">{visit.user_ip || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(stats?.partner_visits || []).length === 0 && (
                  <p className="text-center text-gray-500 py-8">No partner clicks recorded yet.</p>
                )}
              </div>
            </div>
          )}

          {/* Trials Tab */}
          {activeTab === 'trials' && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Trial Users ({stats?.trial_users?.length || 0})
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left p-3 font-semibold">Email</th>
                      <th className="text-left p-3 font-semibold">Calculator</th>
                      <th className="text-left p-3 font-semibold">Financial Lab</th>
                      <th className="text-left p-3 font-semibold">Consulting</th>
                      <th className="text-left p-3 font-semibold">Started</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(stats?.trial_users || []).map((user, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="p-3 font-medium">{user.email}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${user.ebitda_calculator_uses > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {user.ebitda_calculator_uses || 0} uses
                          </span>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${user.financial_lab_uses > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {user.financial_lab_uses || 0} uses
                          </span>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${user.strategic_consulting_uses > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {user.strategic_consulting_uses || 0} uses
                          </span>
                        </td>
                        <td className="p-3 text-gray-600">{formatDate(user.start_time)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(stats?.trial_users || []).length === 0 && (
                  <p className="text-center text-gray-500 py-8">No trial users yet.</p>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p>Admin Dashboard • Smart QRM Navigator</p>
          <p className="mt-1">Data refreshes on page load • Click "Refresh Data" for latest stats</p>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
